/**
 * @file LegalTargetField.tsx
 * @description Поле «Документ или группа документов» (phase19.16, партия
 * «Юридические документы»; uiux-legal-steps-b-c §8.1; SB139, SB141): выбор
 * цели пункта меню или кнопки секции из списка «Группы» / «Документы» с
 * видом и состоянием; «+ Создать» автоматической группы сертификатов, если
 * её нет (создаёт, публикует и сразу выбирает); без права настроек
 * магазина — подсказка вместо «Создать»; под полем — предупреждение о
 * выбранной пустой группе. Список — `legalTargetOptions` (общий с подвалом).
 */

import { Button } from "../../ui/button";
import { Combobox } from "../../ui/combobox";
import { FieldHint } from "../../ui/settings-groups";
import { useLegalTargets } from "../../../hooks/site-view/useLegalTargets";
import {
  CERTIFICATES_NO_RIGHT_HINT,
  CREATE_CERTIFICATES_VALUE,
  EMPTY_GROUP_HINT,
  isCertificatesGroup,
  isEmptyLegalGroup,
  legalTargetOptions,
} from "../../../lib/site-view/legal-targets";

interface Props {
  /** id кнопки-триггера — для `<Label htmlFor>`. */
  id?: string;
  value: string | null;
  /** Выбрана цель: id и название (подставляется в подпись пункта). */
  onChange: (id: string, title: string) => void;
  invalid?: boolean;
  /** Подпись для скринридера, если видимой подписи нет. */
  ariaLabel?: string;
  /** Пункт меню: подсказка, что оферта и политика уже стоят внизу подвала. */
  menuHint?: boolean;
}

export function LegalTargetField({ id, value, onChange, invalid, ariaLabel, menuHint = false }: Props) {
  const legal = useLegalTargets();
  const { targets } = legal;

  if (legal.failed) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="type-caption text-on-surface-variant">Не удалось загрузить документы.</p>
        <Button type="button" variant="outline" onClick={legal.reload}>
          Повторить
        </Button>
      </div>
    );
  }

  const options = targets ? legalTargetOptions({ targets, canCreateCertificates: legal.canCreate }) : [];
  const picked = targets?.find((t) => t.id === value) ?? null;
  const noCertificates = targets !== null && !targets.some(isCertificatesGroup);

  async function pick(next: string | null) {
    if (next === null) return;
    if (next === CREATE_CERTIFICATES_VALUE) {
      const created = await legal.createCertificates();
      if (created) onChange(created.id, created.title);
      return;
    }
    const target = targets?.find((t) => t.id === next);
    if (target) onChange(target.id, target.title);
  }

  return (
    <div className="flex flex-col gap-1">
      <Combobox
        id={id}
        ariaLabel={ariaLabel}
        options={options}
        value={value || null}
        invalid={invalid}
        disabled={legal.creating}
        placeholder={legal.creating ? "Создаём страницу…" : targets ? "Выберите документ или группу" : "Загружаем…"}
        searchPlaceholder="Поиск по названию…"
        emptyText="Документов пока нет — создайте их в «Юридических документах»"
        onChange={(next) => void pick(next)}
      />
      {picked && isEmptyLegalGroup(picked) ? <FieldHint className="mt-0">{EMPTY_GROUP_HINT}</FieldHint> : null}
      {noCertificates && !legal.canCreate ? <FieldHint className="mt-0">{CERTIFICATES_NO_RIGHT_HINT}</FieldHint> : null}
      {menuHint ? <FieldHint className="mt-0">Оферта и политика ПДн всегда стоят в нижней полосе подвала.</FieldHint> : null}
    </div>
  );
}
